import View from './view';
import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {getFormValues} from 'redux-form';

import {CreateRequestFormLimits, IOwnProps, IStateProps} from './model';
import {reduxForm, SubmissionError, reset} from 'redux-form';
import {Forms} from '../../../../../shared/models/forms';
import {RequestForm} from '../../../../../shared/models/supportService';
import {validationPipe} from '../../../../../shared/pipes';
import {createRequest} from '../../../../../redux/actions/requests';
import {PickerResult} from '../../../../../shared/components/filePicker/model';
import {checkFilesFormats, checkFilesMaxSize, removeDuplicates} from './utils';
import {addNotification} from '../../../../../redux/actions/snackbar';
import {SnackbarNotificationType} from '../../../../../shared/components/snackbar/model';
import {feedbackThemesSelector} from '../../../../../redux/selectors/resources';
import {getFeedbackThemes} from '../../../../../redux/actions/resources';

export const REQUEST_MAX_UPLOAD_FILES_COUNT = 10;
export const REQUEST_MAX_UPLOAD_FILE_SIZE_MB = 14;
export const REQUEST_FILE_FORMATS: string[] = ['pdf', 'png', 'jpg', 'jpeg', 'jpe'];
export const REQUEST_MESSAGE_MIN_LENGTH = 10;
export const REQUEST_MESSAGE_MAX_LENGTH = 2000;

const getFormErrors = (formData: RequestForm, {messageMinLength, messageMaxLength}: CreateRequestFormLimits) =>
  validationPipe(formData, [
    {
      names: 'topic',
      error: 'Обязательное поле',
      type: 'required',
    },
    {
      names: 'text',
      error: 'Обязательное поле',
      type: 'required',
    },
    {
      names: 'text',
      error: `Минимальное количество символов ${messageMinLength}`,
      type: 'custom',
      validator: (val: string) => {
        return (val?.length ?? 0) >= messageMinLength;
      },
    },
    {
      names: 'text',
      error: `Максимальное количество символов ${messageMaxLength}`,
      type: 'custom',
      validator: (val: string) => {
        return (val?.length ?? 0) <= messageMaxLength;
      },
    },
  ]);

const Form = reduxForm<RequestForm, IStateProps>({
  form: Forms.createRequest,
})(View);

export const CreateRequestForm: React.FC<IOwnProps> = ({onSuccessSubmit}) => {
  const dispatch = useDispatch();
  const feedbackThemes = useSelector(feedbackThemesSelector);
  const [isLoading, setLoadingStatus] = useState(false);
  const [uploadFilesActive, setUploadFilesStatus] = useState(false);
  const [pickedFiles, setPickedFiles] = useState<PickerResult[]>([]);
  const requestFormInfo = useSelector((state) => getFormValues(Forms.createRequest)(state)) as RequestForm;

  const limits: CreateRequestFormLimits = {
    authorizedFileFormats: REQUEST_FILE_FORMATS,
    maxFileSizeMB: REQUEST_MAX_UPLOAD_FILE_SIZE_MB,
    maxFilesCount: REQUEST_MAX_UPLOAD_FILES_COUNT,
    messageMinLength: REQUEST_MESSAGE_MIN_LENGTH,
    messageMaxLength: REQUEST_MESSAGE_MAX_LENGTH,
  };

  useEffect(() => {
    if (!feedbackThemes) {
      dispatch(getFeedbackThemes());
    }
  }, [!feedbackThemes]);

  const handlePickerUploadError = useCallback(() => {
    setUploadFilesStatus(false);
  }, []);

  const handlePickerUploadStart = useCallback(() => {
    setUploadFilesStatus(true);
  }, []);

  const handlePickerUploadEnd = useCallback(
    (newFiles: PickerResult[]) => {
      const withoutDuplicates = removeDuplicates(pickedFiles, newFiles);
      let rejectedBySize = [];
      let [accepted, rejectedByFormats] = checkFilesFormats(
        [...pickedFiles, ...withoutDuplicates],
        limits.authorizedFileFormats,
      );
      [accepted, rejectedBySize] = checkFilesMaxSize(accepted, limits.maxFileSizeMB);
      const rejected = [...rejectedBySize, ...rejectedByFormats];
      const limit = limits.maxFilesCount - accepted.length;

      if (rejected.length > 0) {
        const authorizedFileFormatsMessage = limits.authorizedFileFormats.join(', ');
        let errorMessage = '';
        if (rejectedByFormats.length) {
          const filenames = rejectedByFormats.map((rejectedFile) => rejectedFile.file.name).join(', ');
          errorMessage =
            rejectedByFormats.length === 1
              ? `Файл ${filenames} не добавлен: формат файла не входит в список разрешенных форматов - ${authorizedFileFormatsMessage}.`
              : `Файлы ${filenames} не добавлены: форматы файлов не входят в список разрешенных форматов - ${authorizedFileFormatsMessage}.`;
        }
        if (rejectedBySize.length) {
          const filenames = rejectedBySize.map((rejectedFile) => rejectedFile.file.name).join(', ');
          errorMessage =
            rejectedBySize.length === 1
              ? `Файл ${filenames} не добавлен: общий размер файлов не должен превышать ${limits.maxFileSizeMB} Мб.`
              : `Файлы ${filenames} не добавлены: общий размер файлов не должен превышать ${limits.maxFileSizeMB} Мб.`;
        }
        dispatch(
          addNotification({
            message: errorMessage,
            type: SnackbarNotificationType.Error,
          }),
        );
      }

      if (limit >= 0) {
        setPickedFiles(accepted);
      } else {
        dispatch(
          addNotification({
            message: `Превышено допустимое количество файлов.`,
            type: SnackbarNotificationType.Error,
          }),
        );
      }

      setUploadFilesStatus(false);
    },
    [pickedFiles],
  );

  const handleDeleteRemove = useCallback(
    (index: number) => {
      const newList = [...pickedFiles];
      newList.splice(index, 1);
      setPickedFiles(newList);
    },
    [pickedFiles],
  );

  const resetForm = useCallback(() => {
    dispatch(reset(Forms.createRequest));
    setPickedFiles([]);
  }, []);

  return React.createElement(Form, {
    emptyRequest: !requestFormInfo?.text || !requestFormInfo?.topic,
    limits,
    isLoading,
    pickedFiles,
    uploadFilesActive,
    feedbackThemes: feedbackThemes || [],
    onPickedFileRemove: handleDeleteRemove,
    onFilePickerUploadError: handlePickerUploadError,
    onFilePickerUploadEnd: handlePickerUploadEnd,
    onFilePickerUploadStart: handlePickerUploadStart,
    async onSubmit(formData: RequestForm) {
      const formErrors = getFormErrors(formData, limits);
      if (formErrors) {
        throw new SubmissionError(formErrors);
      }

      const requestData = {
        ...formData,
        themeId: feedbackThemes?.find(({name}) => name === formData.topic)?.id ?? 0,
      };

      try {
        setLoadingStatus(true);
        await dispatch(createRequest(requestData, pickedFiles));
        resetForm();
        onSuccessSubmit();
        dispatch(
          addNotification({
            message: `Ваше обращение зарегистрировано`,
            type: SnackbarNotificationType.Success,
          }),
        );
      } finally {
        setLoadingStatus(false);
      }
    },
  });
};
